import { FlashcardMeta } from '../models/types';
import { hasMeaningfulNoteContent } from '../utils/note-content';
import { NodeRenderer, NodeRenderContext, NodeRenderResult, continueChain } from './node-renderer';

const indicatorCache = new WeakMap<HTMLElement, string>();
const relayoutPending = new WeakSet<object>();

export class FlashcardBadgeRenderer implements NodeRenderer {
  constructor(
    private readonly emitClick?: (nodeId: string, count: number) => void,
    private readonly emitPaperClick?: (nodeId: string) => void
  ) {}

  render(context: NodeRenderContext): NodeRenderResult {
    const { jm, ele, node } = context;
    if (!(ele instanceof HTMLElement)) {
      return continueChain;
    }
    const data = (node?.data ?? {}) as FlashcardMeta;
    const nested = data.data ?? null;
    const flashcardsCount = Number(nested?.flashcardsCount ?? data.flashcardsCount ?? 0);
    const hasFlashcards = Boolean(nested?.hasFlashcards) || Boolean(data.hasFlashcards) || flashcardsCount > 0;
    const note = nested?.note ?? data.note;
    const hasNote = hasMeaningfulNoteContent(note);
    const hasIndicators = hasFlashcards || hasNote;

    const indicatorKey = `${hasFlashcards ? '1' : '0'}|${hasNote ? '1' : '0'}|${flashcardsCount}`;
    const cachedKey = indicatorCache.get(ele);
    const existingGroup = ele.querySelector('.flashcard-indicators') as HTMLElement | null;
    const existingLegacy = !existingGroup
      ? (ele.querySelector('.flashcard-indicator') as HTMLElement | null)
      : null;

    if (!hasIndicators) {
      if (existingGroup) {
        existingGroup.remove();
      }
      if (existingLegacy) {
        existingLegacy.remove();
      }
      ele.classList.remove('has-flashcards');
      indicatorCache.set(ele, indicatorKey);
      if (cachedKey !== indicatorKey) {
        this.scheduleRelayout(jm);
      }
      return continueChain;
    }

    ele.classList.add('has-flashcards');
    if (cachedKey === indicatorKey && existingGroup) {
      return continueChain;
    }
    if (existingGroup) {
      existingGroup.remove();
    }
    if (existingLegacy) {
      existingLegacy.remove();
    }

    const indicatorGroup = document.createElement('span');
    indicatorGroup.className = 'flashcard-indicators';
    const stopDrag = (event: Event) => {
      event.stopPropagation();
    };

    if (hasNote) {
      const paperButton = document.createElement('span');
      paperButton.className = 'flashcard-paper-indicator';
      paperButton.setAttribute('role', 'button');
      paperButton.setAttribute('tabindex', '0');
      paperButton.setAttribute('aria-label', 'Abrir anotacoes');
      paperButton.setAttribute('title', 'Abrir anotacoes');
      paperButton.innerHTML =
        '<svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M19,14.5 L19,5.5 C19,4.67157288 18.3284271,4 17.5,4 L6.5,4 C5.67157288,4 5,4.67157288 5,5.5 L5,18.5 C5,19.3284271 5.67157288,20 6.5,20 L13.5,20 C14.3284271,20 15,19.3284271 15,18.5 C15,17.1192881 16.1192881,16 17.5,16 C18.3284271,16 19,15.3284271 19,14.5 L19,14.5 Z M18.5014408,16.7913481 C18.1948298,16.9255432 17.8561101,17 17.5,17 C16.6715729,17 16,17.6715729 16,18.5 C16,18.8561101 15.9255432,19.1948298 15.7913481,19.5014408 C16.9873685,18.9526013 17.9526013,17.9873685 18.5014408,16.7913481 L18.5014408,16.7913481 Z M4,5.5 C4,4.11928813 5.11928813,3 6.5,3 L17.5,3 C18.8807119,3 20,4.11928813 20,5.5 L20,14.5 C20,18.0898509 17.0898509,21 13.5,21 L6.5,21 C5.11928813,21 4,19.8807119 4,18.5 L4,5.5 Z M8.5,9 C8.22385763,9 8,8.77614237 8,8.5 C8,8.22385763 8.22385763,8 8.5,8 L15.5,8 C15.7761424,8 16,8.22385763 16,8.5 C16,8.77614237 15.7761424,9 15.5,9 L8.5,9 Z M8.5,12 C8.22385763,12 8,11.7761424 8,11.5 C8,11.2238576 8.22385763,11 8.5,11 L15.5,11 C15.7761424,11 16,11.2238576 16,11.5 C16,11.7761424 15.7761424,12 15.5,12 L8.5,12 Z M8.5,15 C8.22385763,15 8,14.7761424 8,14.5 C8,14.2238576 8.22385763,14 8.5,14 L13.5,14 C13.7761424,14 14,14.2238576 14,14.5 C14,14.7761424 13.7761424,15 13.5,15 L8.5,15 Z"/></svg>';
      paperButton.onpointerdown = stopDrag;
      paperButton.onmousedown = stopDrag;
      paperButton.ontouchstart = stopDrag;
      paperButton.onclick = event => {
        event.stopPropagation();
        this.emitPaperClick?.(node.id);
      };
      indicatorGroup.appendChild(paperButton);
    }

    if (hasFlashcards) {
      const indicator = document.createElement('span');
      indicator.className = 'flashcard-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      indicator.setAttribute('title', `Flashcards: ${flashcardsCount || 1}`);
      const displayCount = Math.min(Math.max(flashcardsCount || 1, 1), 99);
      indicator.textContent = displayCount === 99 ? '99+' : String(displayCount);
      indicator.setAttribute('role', 'button');
      indicator.setAttribute('tabindex', '0');
      indicator.setAttribute('aria-label', `Abrir flashcards (${displayCount})`);
      indicator.onpointerdown = stopDrag;
      indicator.onmousedown = stopDrag;
      indicator.ontouchstart = stopDrag;
      indicator.onclick = event => {
        event.stopPropagation();
        const count = flashcardsCount || 1;
        this.emitClick?.(node.id, count);
      };
      indicatorGroup.appendChild(indicator);
    }

    ele.appendChild(indicatorGroup);
    indicatorCache.set(ele, indicatorKey);
    if (cachedKey !== indicatorKey) {
      this.scheduleRelayout(jm);
    }

    return continueChain;
  }

  private scheduleRelayout(jm: unknown): void {
    const jmObj = jm as { layout?: { layout?: () => void }; view?: { relayout?: () => void } } | null;
    if (!jmObj || typeof jmObj !== 'object') {
      return;
    }
    if (relayoutPending.has(jmObj)) {
      return;
    }
    relayoutPending.add(jmObj);
    const run = () => {
      relayoutPending.delete(jmObj);
      if (typeof jmObj.layout?.layout === 'function') {
        jmObj.layout.layout();
      }
      if (typeof jmObj.view?.relayout === 'function') {
        jmObj.view.relayout();
      }
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }
}
